import { useState, useEffect } from "react";
import { getKpis, getCaseStudies, type KpiItem, type CaseStudyItem } from "../utils/adminStore";

export function ImpactStats() {
  const [kpis, setKpis] = useState<KpiItem[]>([]);
  const [caseStudies, setCaseStudies] = useState<CaseStudyItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    const sync = () => {
      setKpis(getKpis());
      setCaseStudies(getCaseStudies());
    };
    sync();
    window.addEventListener("radiece_admin_update", sync);
    return () => window.removeEventListener("radiece_admin_update", sync);
  }, []);

  const activeCase = caseStudies[selectedIndex] || caseStudies[0];

  return (
    <section className="no-print bg-slate-900 py-16 sm:py-20 text-white relative overflow-hidden border-y border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Engineering Commitments & Metrics Grid */}
        {kpis.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-16 pb-16 border-b border-slate-800">
            {kpis.map((item) => (
              <div key={item.id} className="bg-[#101c2a] rounded-xl p-6 border border-white/10 text-center shadow-lg">
                <p className="text-3xl sm:text-4xl font-extrabold text-primary mb-1">{item.metric}</p>
                <p className="text-xs sm:text-sm text-slate-200 font-bold uppercase tracking-wider">{item.label}</p>
                <p className="text-[11px] text-slate-400 font-light mt-1">{item.subtext}</p>
              </div>
            ))}
          </div>
        )}

        {/* Reference Engineering Blueprints Feature */}
        {caseStudies.length > 0 && activeCase && (
          <div>
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                Technical Architecture Blueprints
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Featured Engineering System Specifications
              </h2>
            </div>

            {/* Tab Selector Buttons */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {caseStudies.map((cs, idx) => (
                <button
                  key={cs.id}
                  onClick={() => setSelectedIndex(idx)}
                  className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold uppercase tracking-wider transition-all duration-200 border cursor-pointer ${
                    selectedIndex === idx
                      ? "bg-primary text-slate-950 border-primary shadow-md shadow-primary/20"
                      : "bg-[#101c2a] text-slate-300 border-white/15 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {cs.category}
                </button>
              ))}
            </div>

            {/* Active Case Details Card */}
            <div className="max-w-4xl mx-auto bg-[#101c2a] rounded-2xl border border-white/15 p-6 sm:p-8 backdrop-blur-sm shadow-2xl">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <span className="text-xs font-bold text-primary uppercase tracking-wider">
                  Domain: {activeCase.category}
                </span>
                <span className="px-3 py-1 rounded-full bg-sky-500/20 border border-sky-500/30 text-sky-300 text-xs font-bold">
                  ● Status: {activeCase.impact}
                </span>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                {activeCase.title}
              </h3>

              <p className="text-sm sm:text-base text-slate-300 leading-relaxed font-light mb-6">
                {activeCase.summary}
              </p>

              <div className="pt-4 border-t border-white/10 flex items-center justify-between">
                <span className="text-xs text-slate-400 font-mono">
                  Blueprint Reference: {activeCase.id}
                </span>
                <a
                  href="/contact"
                  className="text-xs font-bold text-primary hover:underline uppercase tracking-wider"
                >
                  Inquire on Engineering Collaboration →
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
